import { HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, Observable, tap } from 'rxjs';

import { SKIP_HTTP_CACHE } from './skip-http-cache';
import { CacheService } from '../services/cache.service';

const MUTATION_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

const STATIC_EXTENSIONS = [
  '.svg', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.ico', '.woff', '.woff2'
];

interface InvalidationRule {
  targets: string[];
  anyMethod?: boolean;
}

/**
 * Path de mutação (substring do pathname) → prefixos de cache a invalidar.
 * Também reaproveitado por provide-cache-invalidation.ts para o evento de
 * socket oferta-estruturada:atualizada (mesmos prefixos de domínio).
 */
export const INVALIDATION_MAP: Record<string, InvalidationRule> = {
  '/oferta-estruturada': { targets: ['/api/oferta-estruturada', '/api/pg/negociacao-compra-venda', '/api/canal', '/api/alert'] },
  '/api/canal/seguir': { targets: ['/api/canal'], anyMethod: true },
  '/api/canal/deixar-de-seguir': { targets: ['/api/canal'], anyMethod: true },
  '/api/canal': { targets: ['/api/canal'] },
  '/api/pg/negociacao-compra-venda': { targets: ['/api/pg/negociacao-compra-venda'] },
  '/api/interactions': { targets: ['/api/pg/negociacao-compra-venda'] },
  '/api/alert/mark-read-all': { targets: ['/api/alert'], anyMethod: true },
  '/api/alert': { targets: ['/api/alert'] },
  '/user/update': { targets: ['/api/auth/me'] }
};

function pathOf(url: string): string {
  try { return new URL(url).pathname; } catch { return url; }
}

function cacheKey(req: HttpRequest<unknown>): string {
  try {
    const url = new URL(req.urlWithParams);
    url.searchParams.sort();
    return url.pathname + url.search;
  } catch { return req.urlWithParams; }
}

function isStaticAsset(url: string): boolean {
  return STATIC_EXTENSIONS.some(ext => url.split('?')[0].endsWith(ext));
}

function matchInvalidationRule(path: string): InvalidationRule | undefined {
  const entry = Object.entries(INVALIDATION_MAP).find(([mutationPath]) =>
    path.includes(mutationPath)
  );
  return entry?.[1];
}

function invalidate(cache: CacheService, targets: string[]) {
  return tap((event: HttpEvent<unknown>) => {
    if (event instanceof HttpResponse) {
      targets.forEach(prefix => cache.invalidateByPrefix(prefix));
    }
  });
}

export function httpCacheInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const cache = inject(CacheService);
  const path = pathOf(req.urlWithParams);

  const rule = matchInvalidationRule(path);
  if (rule && (rule.anyMethod || MUTATION_METHODS.includes(req.method))) {
    return next(req).pipe(invalidate(cache, rule.targets));
  }

  if (req.context.get(SKIP_HTTP_CACHE) || req.method !== 'GET' || isStaticAsset(req.url)) {
    return next(req);
  }

  const key = cacheKey(req);
  const cached = cache.get<HttpResponse<unknown>>(key);
  if (cached) {
    return from(Promise.resolve(cached.clone({ body: structuredClone(cached.body) })));
  }

  return next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse && event.status === 200) {
        cache.set(key, event.clone({ body: structuredClone(event.body) }));
      }
    })
  );
}
